/**
 * ApiTokensCard — manage long-lived API tokens for scripts / mobile / external integrations.
 *
 * Phase E.1 deliverable. The raw token is shown EXACTLY ONCE in a modal at
 * create-time with an explicit "won't be shown again" warning + copy button.
 * Existing tokens are listed with their prefix, last-used timestamp, expiry,
 * and a revoke action.
 */
import React, { useEffect, useState } from 'react';
import {
  Card, CardHeader, CardContent, Box, Typography, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Stack,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useConfirm } from '../common/ConfirmDialog';

interface TokenRow {
  id: number;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  expired: boolean;
}

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ApiTokensCard: React.FC = () => {
  const { C } = useAppTheme();
  const confirm = useConfirm();
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftTtl, setDraftTtl] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [newTokenResult, setNewTokenResult] = useState<{ token: string; name: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<{ success: boolean; data: TokenRow[] }>('/user/api-tokens');
      setTokens(r.data?.data || []);
    } catch (err: any) {
      // Token list non-fatal
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!draftName.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post<{ success: boolean; data: { token: string; name: string } }>('/user/api-tokens', {
        name: draftName.trim(),
        ttlDays: draftTtl === '' ? undefined : draftTtl,
      });
      const d = r.data?.data;
      if (d?.token) {
        setNewTokenResult({ token: d.token, name: d.name });
        setCreateOpen(false);
        setDraftName('');
        setDraftTtl('');
        load();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to create token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (row: TokenRow) => {
    const ok = await confirm({
      title: `Revoke "${row.name}"?`,
      body: 'Any scripts or apps using this token will stop working immediately. This cannot be undone.',
      confirmLabel: 'Revoke',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/user/api-tokens/${row.id}`);
      toast.success(`Revoked "${row.name}"`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to revoke');
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon fontSize="small" />
            <span>API Tokens</span>
          </Box>
        }
        subheader="Long-lived bearer tokens for scripts, mobile apps, and external integrations. Tokens carry the same permissions as your account — treat like a password."
        action={
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="contained"
            onClick={() => setCreateOpen(true)}
            disabled={tokens.filter(t => !t.expired).length >= 10}
          >
            New token
          </Button>
        }
      />
      <CardContent>
        {tokens.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No tokens yet. Create one to authenticate scripts or third-party tools without your password.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Prefix', 'Created', 'Last used', 'Expires', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map(t => (
                <TableRow key={t.id} hover sx={{ opacity: t.expired ? 0.6 : 1 }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.name}</Typography>
                    {t.expired && <Chip label="EXPIRED" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: C.bearMuted, color: C.bear, fontWeight: 700 }} />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      eg_{t.prefix}_…
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption">{fmtDate(t.createdAt)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmtDate(t.lastUsedAt)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{t.expiresAt ? fmtDate(t.expiresAt) : 'Never'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke">
                      <IconButton size="small" onClick={() => handleRevoke(t)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          <strong>Usage:</strong> set request header <code style={{ fontFamily: 'monospace' }}>Authorization: Bearer eg_&lt;prefix&gt;_&lt;secret&gt;</code> on any API endpoint.
          Max 10 active tokens per account.
        </Typography>
      </CardContent>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create API token</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              size="small"
              label="Token name"
              placeholder="e.g. Personal script"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value.slice(0, 100))}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Expires</InputLabel>
              <Select
                value={draftTtl}
                label="Expires"
                onChange={(e) => setDraftTtl(e.target.value as number | '')}
              >
                <MenuItem value="">Never</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
                <MenuItem value={365}>1 year</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="warning" icon={false}>
              The full token will be shown <strong>only once</strong>. Save it somewhere safe before you close the next dialog.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!draftName.trim() || submitting}>
            {submitting ? 'Creating…' : 'Create token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created-token reveal */}
      <Dialog open={!!newTokenResult} onClose={() => setNewTokenResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Token created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Copy this token now — it won't be shown again.</strong> If you lose it, revoke and create a new one.
          </Alert>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Token for "{newTokenResult?.name}":
          </Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1.25, borderRadius: 1, bgcolor: C.dimMuted, border: `1px dashed ${C.dim}`,
          }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', flex: 1, wordBreak: 'break-all' }}>
              {newTokenResult?.token}
            </Typography>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => newTokenResult && handleCopy(newTokenResult.token)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Use as: <code style={{ fontFamily: 'monospace' }}>Authorization: Bearer {newTokenResult?.token}</code>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTokenResult(null)} variant="contained">I've saved it</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ApiTokensCard;
